import { faAngleLeft, faAngleRight, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DateTime } from "luxon";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components/macro";
import {
  getNewsletterEditions,
  NewsletterEdition,
  NewsletterEditionsResponse,
  NewsletterPublication,
} from "../api";
import { IComponent } from "../Component";
import { useLocation } from "../router";
import { bsrBlue } from "./bsr-defs";
import {
  CellState,
  cellState,
  dayRows,
  indexEditions,
  shiftAnchor,
  todayISO,
  visiblePublications,
  windowDates,
  windowLength,
  ZONE,
} from "./BSRNewsletterLogic";

export const BSR_NEWSLETTER_COMPONENT = "bsr_newsletter";

export interface BSRNewsletterComponentSpec {
  type: typeof BSR_NEWSLETTER_COMPONENT;
  /** Publication slugs NOT shown; absent = show all, including newspapers added later. */
  hidden?: string[];
  /** Window length in days, 1–14; absent or out of range = 7. */
  days?: number;
  /** Fields of the old one-newspaper widget. Read for backwards compatibility, never written. */
  newsletter?: string;
  vertical?: boolean;
  dense?: boolean;
}

/* ---------------------------------------------------------------- state */

export function useNewsletterWindow(days: number) {
  const today = todayISO();
  const [anchor, setAnchor] = useState(today);
  const dates = useMemo(() => windowDates(anchor, days), [anchor, days]);
  return {
    today,
    anchor,
    dates,
    from: dates[0],
    to: dates[dates.length - 1],
    canGoForward: anchor < today,
    back: () => setAnchor((a) => shiftAnchor(a, -days, today)),
    forward: () => setAnchor((a) => shiftAnchor(a, days, today)),
    jump: (iso: string) => {
      if (iso) setAnchor(iso > today ? today : iso);
    },
    reset: () => setAnchor(today),
  };
}

type Loaded =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; data: NewsletterEditionsResponse };

export function useNewsletterEditions(path: string, from: string, to: string): Loaded {
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });
  useEffect(() => {
    let cancelled = false;
    setLoaded({ state: "loading" });
    getNewsletterEditions({ path, from, to }).then(
      (data) => {
        if (!cancelled) setLoaded({ state: "ready", data });
      },
      () => {
        if (!cancelled) setLoaded({ state: "error" });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [path, from, to]);
  return loaded;
}

/* ---------------------------------------------------------------- styles */

const Card = styled.div`
  background: white;
  box-shadow: 0 1px 1px 0 rgba(60, 64, 67, 0.08), 0 1px 3px 1px rgba(60, 64, 67, 0.16);
  margin-top: 15px;
  padding: 10px;
`;

const Nav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const NavButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: ${bsrBlue};
  font-size: 1.3em;
  padding: 5px 10px;
  min-width: 44px;
  min-height: 44px;
  :disabled {
    color: #bbb;
    cursor: default;
  }
`;

const DateInput = styled.input`
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  padding: 0;
`;

const TodayButton = styled.button`
  border: 1px solid ${bsrBlue};
  border-radius: 20px;
  background: none;
  color: ${bsrBlue};
  cursor: pointer;
  padding: 3px 10px;
  font: inherit;
`;

/* Safety net only: at 7 days the grid always fits; at 14 on a narrow desktop it may scroll inside the card, never the page. */
const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    text-align: center;
    padding: 2px;
  }
  th {
    font-weight: normal;
    color: #565656;
    font-size: 0.85em;
    padding-bottom: 6px;
  }
  th.today {
    color: ${bsrBlue};
    font-weight: bold;
  }
  th.name,
  td.name {
    text-align: left;
    white-space: nowrap;
    padding-left: 5px;
    width: 1%;
  }
  tbody tr {
    border-top: 1px solid #eee;
  }
`;

const PdfLink = styled.a`
  color: ${bsrBlue};
  display: inline-block;
  min-width: 44px;
  line-height: 44px;
  font-size: 1.4em;
`;

const Missing = styled.span`
  color: #565656;
  display: inline-block;
  min-width: 44px;
  line-height: 44px;
`;

const DayHeading = styled.h3`
  margin: 12px 0 4px;
  font-size: 1em;
  &.today {
    color: ${bsrBlue};
  }
`;

const DayRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #eee;
  min-height: 44px;
  padding: 0 5px;
`;

const RowLink = styled.a`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  min-height: 44px;
  color: inherit;
  text-decoration: none;
  svg {
    color: ${bsrBlue};
    font-size: 1.4em;
  }
`;

/* ---------------------------------------------------------------- pieces */

function fmt(date: string, format: string): string {
  return DateTime.fromISO(date, { zone: ZONE }).setLocale("de-CH").toFormat(format);
}

function Cell(props: { state: CellState; label: string }) {
  switch (props.state.kind) {
    case "edition":
      return (
        <PdfLink href={props.state.edition.url} target="_blank" rel="noopener" title={props.label}>
          <FontAwesomeIcon icon={faFilePdf} />
        </PdfLink>
      );
    case "missing":
      return <Missing title="Keine Ausgabe">○</Missing>;
    default:
      return null;
  }
}

interface LayoutProps {
  publications: NewsletterPublication[];
  dates: string[];
  editions: Map<string, NewsletterEdition>;
  today: string;
}

/** Desktop: newspapers × days. */
function Grid(props: LayoutProps) {
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <th className="name" />
            {props.dates.map((d) => (
              <th key={d} className={d === props.today ? "today" : undefined}>
                {fmt(d, "ccc")}
                <br />
                {fmt(d, "dd.MM.")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.publications.map((p) => (
            <tr key={p.slug}>
              <td className="name">{p.name}</td>
              {props.dates.map((d) => (
                <td key={d}>
                  <Cell
                    state={cellState(p, d, props.editions, props.today)}
                    label={p.name + " " + fmt(d, "dd.MM.yyyy")}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}

/** Mobile: days newest first, each listing only the newspapers with something on that day. */
function DayList(props: LayoutProps) {
  const days = [...props.dates]
    .reverse()
    .map((date) => ({ date, rows: dayRows(props.publications, date, props.editions, props.today) }))
    .filter((day) => day.rows.length > 0);
  return (
    <div>
      {days.map((day) => (
        <div key={day.date}>
          <DayHeading className={day.date === props.today ? "today" : undefined}>
            {fmt(day.date, "ccc dd.MM.yyyy")}
          </DayHeading>
          {day.rows.map((row) => (
            <DayRow key={row.publication.slug}>
              {row.state.kind === "edition" ? (
                <RowLink href={row.state.edition.url} target="_blank" rel="noopener">
                  <span>{row.publication.name}</span>
                  <FontAwesomeIcon icon={faFilePdf} />
                </RowLink>
              ) : (
                <>
                  <span>{row.publication.name}</span>
                  <Missing title="Keine Ausgabe">○</Missing>
                </>
              )}
            </DayRow>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- component */

const Component = (props: { spec: BSRNewsletterComponentSpec }) => {
  const location = useLocation();
  const days = windowLength(props.spec.days);
  const win = useNewsletterWindow(days);
  const loaded = useNewsletterEditions(location, win.from, win.to);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const publications =
    loaded.state === "ready" ? visiblePublications(props.spec, loaded.data.publications) : [];
  const editions = useMemo(
    () => indexEditions(loaded.state === "ready" ? loaded.data.editions : []),
    [loaded]
  );

  return (
    <Card>
      <Nav>
        <NavButton onClick={win.back} title="Zurück">
          <FontAwesomeIcon icon={faAngleLeft} />
        </NavButton>
        <span>{fmt(win.from, "dd.MM.")} –</span>
        <DateInput
          type="date"
          value={win.anchor}
          max={win.today}
          onChange={(e) => win.jump(e.currentTarget.value)}
        />
        <NavButton onClick={win.forward} disabled={!win.canGoForward} title="Vor">
          <FontAwesomeIcon icon={faAngleRight} />
        </NavButton>
        {win.anchor !== win.today ? <TodayButton onClick={win.reset}>Heute</TodayButton> : null}
      </Nav>
      {loaded.state === "loading" ? "Lädt…" : null}
      {loaded.state === "error" ? "Ausgaben momentan nicht verfügbar." : null}
      {loaded.state === "ready" ? (
        isMobile ? (
          <DayList publications={publications} dates={win.dates} editions={editions} today={win.today} />
        ) : (
          <Grid publications={publications} dates={win.dates} editions={editions} today={win.today} />
        )
      ) : null}
    </Card>
  );
};

/** The renderer on its own, for the editor's preview. */
export const BSRNewsletterRender = Component;

export const BSRNewsletterComponent: IComponent<BSRNewsletterComponentSpec> = {
  component: Component,
};
